import React from 'react';
import { cn } from '@/lib/utils';
import type { ClientStage } from '@/types/client';
import { ALL_STAGES, STAGE_LABELS, STAGE_COLORS } from '../shared/stageConstants';

export type StageFilter = ClientStage | 'all';

interface StageFilterBarProps {
  activeFilter: StageFilter;
  stageCounts: Record<ClientStage, number>;
  totalCount: number;
  onFilterChange: (stage: StageFilter) => void;
}

export const StageFilterBar: React.FC<StageFilterBarProps> = ({
  activeFilter,
  stageCounts,
  totalCount,
  onFilterChange,
}) => {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none -mx-1 px-1">
      {/* All filter */}
      <button
        onClick={() => onFilterChange('all')}
        aria-pressed={activeFilter === 'all'}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-label uppercase tracking-wider whitespace-nowrap transition-colors',
          'border border-border',
          activeFilter === 'all'
            ? 'bg-primary/15 text-primary border-primary/30'
            : 'bg-surface-2 text-muted-foreground hover:bg-surface-3 hover:text-foreground'
        )}
      >
        All
        <span className="font-mono text-[10px] opacity-70">{totalCount}</span>
      </button>

      {ALL_STAGES.map((stage) => {
        const colors = STAGE_COLORS[stage];
        const count = stageCounts[stage] || 0;
        const isActive = activeFilter === stage;

        return (
          <button
            key={stage}
            onClick={() => onFilterChange(stage)}
            aria-pressed={isActive}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-label uppercase tracking-wider whitespace-nowrap transition-colors',
              'border',
              isActive
                ? `${colors.bg} ${colors.text} border-current/30`
                : 'bg-surface-2 text-muted-foreground border-border hover:bg-surface-3 hover:text-foreground'
            )}
          >
            <span
              className={cn(
                'w-1.5 h-1.5 rounded-full flex-shrink-0',
                isActive ? colors.dot : 'bg-current opacity-40'
              )}
            />
            {STAGE_LABELS[stage]}
            <span className="font-mono text-[10px] opacity-70">{count}</span>
          </button>
        );
      })}
    </div>
  );
};
